import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Stack, Button, ButtonBase, CircularProgress, Alert, Link, Paper, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, Snackbar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '../services/api';
import { taskApi, TaskListItem, TaskDetail, TaskStatus, TaskPriority } from '../services/taskService';
import { STATUS_LABEL, PRIORITY_LABEL } from './tasks/taskShared';
import { useSalonContextStore } from '../store/salonContextStore';
import { tokens } from '../theme';
import {
  PageHeader, StatTile, SectionCard, EmptyState, LoadingState, StatusPill, BookingStatusPill, PersonAvatar, PillTone,
} from '../components/ui';
import {
  formatMoney, formatNumber, formatPercent, formatDate, formatTime, formatDateLong, formatDuration, countLabel, plural,
} from '../utils/format';

// Booking.startTime/endTime to stringi "HH:mm" — pełny moment w czasie budujemy z pola date
const bookingStart = (b: { date?: string; startTime?: string }) =>
  new Date(`${(b.date || '').slice(0, 10)}T${b.startTime || '00:00'}:00`);
const bookingEnd = (b: { date?: string; endTime?: string }) =>
  new Date(`${(b.date || '').slice(0, 10)}T${b.endTime || '23:59'}:00`);

const OPEN_TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'ON_HOLD', 'IN_REVIEW', 'IN_APPROVAL', 'NEEDS_REWORK'];

const TASK_STATUS_TONE: Record<TaskStatus, PillTone> = {
  TODO: 'neutral',
  IN_PROGRESS: 'plum',
  ON_HOLD: 'warning',
  IN_REVIEW: 'info',
  IN_APPROVAL: 'info',
  NEEDS_REWORK: 'danger',
  DONE: 'success',
  CANCELLED: 'neutral',
};

const PRIORITY_DOT: Record<TaskPriority, string> = {
  LOW: tokens.subtle,
  NORMAL: tokens.info,
  HIGH: tokens.warning,
  URGENT: tokens.danger,
};

// Strony kalendarza i kasy sa dostepne tylko dla tych rol (App.tsx).
const OPERATOR_ROLES = ['SALON_OWNER', 'STAFF'];
const EMPLOYEE_VIEW_ROLES = ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ACCOUNTANT'];

const getGreeting = (firstName?: string | null) => {
  const base = new Date().getHours() < 18 ? 'Dzień dobry' : 'Dobry wieczór';
  return firstName ? `${base}, ${firstName}` : base;
};

const personName = (u?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.email || '';

/* ---------- WSPOLNE ELEMENTY ---------- */

/** Klikalna karta skrotu: ikona, nazwa strony i jedno zdanie opisu. */
const ShortcutCard: React.FC<{ icon: React.ReactNode; title: string; description: string; onClick: () => void }> = ({
  icon, title, description, onClick,
}) => (
  <Paper
    sx={{
      height: '100%',
      transition: 'border-color .15s ease, box-shadow .15s ease',
      '&:hover': { borderColor: tokens.lineStrong, boxShadow: 3 },
      '&:hover .shortcut-arrow': { color: tokens.plum, transform: 'translateX(2px)' },
    }}
  >
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%', height: '100%', p: { xs: 2, md: 2.5 }, gap: 1.75,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 22 } }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.25 }}>{description}</Typography>
      </Box>
      <ArrowForwardIcon className="shortcut-arrow" sx={{ fontSize: 20, color: tokens.subtle, mt: 1, transition: 'color .15s ease, transform .15s ease' }} />
    </ButtonBase>
  </Paper>
);

const ProgressRow: React.FC<{ label: string; valueText: string; value: number; color?: string }> = ({ label, valueText, value, color }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} sx={{ mb: 0.75 }}>
      <Typography variant="body2" sx={{ color: tokens.inkSoft, fontWeight: 600 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{valueText}</Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.max(0, Math.min(100, value))}
      sx={color ? { '& .MuiLinearProgress-bar': { bgcolor: color } } : undefined}
    />
  </Box>
);

const MiniStat: React.FC<{ value: number; label: string; color?: string }> = ({ value, label, color }) => (
  <Box sx={{ px: 1.25, py: 1.25, borderRadius: '12px', bgcolor: tokens.surface, border: `1px solid ${tokens.line}`, minWidth: 0 }}>
    <Typography sx={{ fontSize: '1.375rem', fontWeight: 750, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', color: value > 0 && color ? color : tokens.ink }}>
      {formatNumber(value)}
    </Typography>
    <Typography variant="caption" component="div" sx={{ mt: 0.25, fontSize: '0.75rem', lineHeight: 1.35 }}>{label}</Typography>
  </Box>
);

const tilesGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
  mb: { xs: 2.5, md: 3 },
} as const;

/* ---------- SUPER ADMIN ---------- */
const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ salons: 0, bookings: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // API przyjmuje limit 1–100; liczbe salonow bierzemy z paginacji.
    apiClient.get('/salons', { params: { limit: 100 } })
      .then((r) => setStats((s) => ({ ...s, salons: r.data.pagination?.total ?? (r.data.data || []).length })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      <PageHeader
        eyebrow={formatDateLong(new Date())}
        title="Panel platformy"
        description="Salony korzystające z Visitio i statystyki całej platformy."
        actions={<Button variant="contained" onClick={() => navigate('/admin/salons')}>Zarządzaj salonami</Button>}
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        <StatTile
          label="Salony na platformie"
          value={formatNumber(stats.salons)}
          hint="Otwórz listę salonów w systemie"
          icon={<StorefrontOutlinedIcon />}
          loading={loading}
          onClick={() => navigate('/admin/salons')}
        />
        <ShortcutCard
          icon={<InsightsOutlinedIcon />}
          title="Statystyki platformy"
          description="Rejestracje, wizyty i przychody wszystkich salonów."
          onClick={() => navigate('/admin/stats')}
        />
        <ShortcutCard
          icon={<TaskAltOutlinedIcon />}
          title="Zadania"
          description="Zadania zespołu i terminy do pilnowania."
          onClick={() => navigate('/tasks')}
        />
      </Box>
    </Box>
  );
};

/* ---------- SALON OWNER ---------- */
const SalonOwnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { activeSalonId, loadSalons } = useSalonContextStore();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ bookingsToday: 0, clients: 0, services: 0, revenue: 0 });
  const [todayBookings, setTodayBookings] = useState<any[]>([]);
  const [staffMembers, setStaffMembers] = useState<any[]>([]);
  const [salonName, setSalonName] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [dueTodayTasks, setDueTodayTasks] = useState<TaskListItem[]>([]);
  const [dashStats, setDashStats] = useState({ monthBookings: 0, totalBookings: 0, monthRevenue: 0, yearRevenue: 0 });
  const [taskDetail, setTaskDetail] = useState<TaskDetail | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const role = user?.role || '';
  const canOperate = OPERATOR_ROLES.includes(role);
  const canOpenEmployee = EMPLOYEE_VIEW_ROLES.includes(role);

  const openTaskDetail = async (id: string) => {
    setTaskDetailLoading(true);
    try {
      setTaskDetail(await taskApi.get(id));
    } catch { /* szczegóły niedostępne — zostaw zamknięte */ }
    finally { setTaskDetailLoading(false); }
  };

  // Cel dnia (przychód / wizyty) — zapisywany lokalnie per salon
  const [dailyGoal, setDailyGoal] = useState<{ revenue: number; visits: number }>({ revenue: 0, visits: 0 });
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ revenue: '', visits: '' });

  useEffect(() => {
    if (!activeSalonId) return;
    try {
      const saved = JSON.parse(localStorage.getItem(`visitio.dailyGoal.${activeSalonId}`) || 'null');
      setDailyGoal(saved && typeof saved === 'object' ? { revenue: Number(saved.revenue) || 0, visits: Number(saved.visits) || 0 } : { revenue: 0, visits: 0 });
    } catch { setDailyGoal({ revenue: 0, visits: 0 }); }
  }, [activeSalonId]);

  const openGoalDialog = () => {
    setGoalForm({
      revenue: dailyGoal.revenue ? String(dailyGoal.revenue) : '',
      visits: dailyGoal.visits ? String(dailyGoal.visits) : '',
    });
    setGoalOpen(true);
  };

  const saveGoal = () => {
    const g = { revenue: Number(goalForm.revenue) || 0, visits: Number(goalForm.visits) || 0 };
    setDailyGoal(g);
    if (activeSalonId) {
      try { localStorage.setItem(`visitio.dailyGoal.${activeSalonId}`, JSON.stringify(g)); } catch { /* ignore */ }
    }
    setGoalOpen(false);
  };

  const copyBookingLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingLink);
      setToast({ message: 'Skopiowano link do rezerwacji online', severity: 'success' });
    } catch {
      setToast({ message: `Nie udało się skopiować. Link: ${bookingLink}`, severity: 'error' });
    }
  };

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  useEffect(() => {
    const load = async () => {
      try {
        if (!activeSalonId) {
          return;
        }

        const salonRes = await apiClient.get(`/salons/${activeSalonId}`).catch(() => ({ data: { name: '' } }));
        const salonId = activeSalonId;
        setSalonName(salonRes.data?.name || '');
        const salonData = salonRes.data as { slug?: string | null; allowOnlineBooking?: boolean };
        setBookingLink(salonData?.slug && salonData.allowOnlineBooking !== false ? `${window.location.origin}/s/${salonData.slug}/rezerwacja` : '');
        const today = new Date().toISOString().slice(0, 10);

        const [bookingsRes, clientsRes, servicesRes, staffRes, salesSummaryRes] = await Promise.all([
          // Poprawny endpoint to GET /bookings?salonId=... (ścieżka /bookings/salon/:id nie istnieje)
          apiClient.get('/bookings', { params: { salonId, date: today, limit: 100 } }).catch(() => ({ data: { data: [] } })),
          apiClient.get(`/clients/salon/${salonId}`).catch(() => ({ data: { data: [] } })),
          apiClient.get(`/services/salon/${salonId}`).catch(() => ({ data: [] })),
          apiClient.get(`/staff/salon/${salonId}`).catch(() => ({ data: [] })),
          apiClient.get('/sales/summary', { params: { salonId } }).catch(() => ({ data: { todayRevenue: 0 } })),
        ]);

        taskApi.list({ salonId, limit: 100 })
          .then((res) => setTasks(res.data.filter((t) => OPEN_TASK_STATUSES.includes(t.status) && !t.isArchived)))
          .catch(() => setTasks([]));
        taskApi.list({ salonId, dueFrom: `${today}T00:00:00`, dueTo: `${today}T23:59:59`, limit: 100 })
          .then((res) => setDueTodayTasks(res.data.filter((t) => !t.isArchived && t.status !== 'CANCELLED')))
          .catch(() => setDueTodayTasks([]));
        apiClient.get('/bookings/stats', { params: { salonId } })
          .then((r) => setDashStats({
            monthBookings: Number(r.data.monthBookings) || 0,
            totalBookings: Number(r.data.totalBookings) || 0,
            monthRevenue: Number(r.data.monthRevenue) || 0,
            yearRevenue: Number(r.data.yearRevenue) || 0,
          }))
          .catch(() => { /* kafelki pokażą zera */ });

        const bookings = bookingsRes.data.data || bookingsRes.data || [];
        const clients = clientsRes.data.data || clientsRes.data || [];
        const services = Array.isArray(servicesRes.data) ? servicesRes.data : servicesRes.data.data || [];
        const staff = Array.isArray(staffRes.data) ? staffRes.data : staffRes.data.data || [];

        const completed = bookings.filter((b: any) => b.status === 'COMPLETED');
        // totalPrice to Prisma Decimal serializowany jako string — bez Number() suma skleja napisy
        const bookingRevenue = completed.reduce((sum: number, b: any) => sum + (Number(b.totalPrice) || 0), 0);
        const salesRevenue = Number(salesSummaryRes.data.todayRevenue) || 0;
        const revenue = bookingRevenue + salesRevenue;

        setStats({
          bookingsToday: bookings.length,
          // Odpowiedź jest stronicowana (domyślnie 20 rekordów) — licznik musi brać total z paginacji
          clients: clientsRes.data.pagination?.total ?? clients.length,
          services: services.length,
          revenue,
        });
        setTodayBookings(bookings.sort((a: any, b: any) => (a.startTime || '').localeCompare(b.startTime || '')));
        setStaffMembers(staff);
      } catch {
        // stats fail silently
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeSalonId]);

  const now = new Date();
  const upcoming = todayBookings.filter((b) =>
    bookingStart(b) > now && !['CANCELLED', 'NO_SHOW', 'COMPLETED'].includes(b.status)
  );
  const completedCount = todayBookings.filter((b) => b.status === 'COMPLETED').length;
  const cancelledCount = todayBookings.filter((b) => b.status === 'CANCELLED' || b.status === 'NO_SHOW').length;
  // Klienci, którzy mają dziś przyjść (bez anulowanych i nieobecnych), bez duplikatów
  const todayClientsCount = new Set(
    todayBookings.filter((b) => !['CANCELLED', 'NO_SHOW'].includes(b.status)).map((b) => b.clientId).filter(Boolean),
  ).size;
  const occupancy = stats.bookingsToday > 0 ? Math.round((completedCount / stats.bookingsToday) * 100) : 0;
  const dueDone = dueTodayTasks.filter((t) => t.status === 'DONE').length;
  const overdueCount = tasks.filter((t) => t.isOverdue).length;
  const revenueGoalPct = dailyGoal.revenue > 0 ? Math.round((stats.revenue / dailyGoal.revenue) * 100) : 0;
  const visitsGoalPct = dailyGoal.visits > 0 ? Math.round((completedCount / dailyGoal.visits) * 100) : 0;

  const activeToday = todayBookings.filter((b) => !['CANCELLED', 'NO_SHOW'].includes(b.status));
  const lastStart = activeToday.length ? activeToday[activeToday.length - 1].startTime : null;
  const dayState = activeToday.length
    ? `dziś ${countLabel(todayBookings.length, 'wizyta', 'wizyty', 'wizyt')}${lastStart ? `, ostatnia o ${lastStart}` : ''}`
    : 'dziś bez zaplanowanych wizyt';

  const visibleBookings = showAllBookings ? todayBookings : todayBookings.slice(0, 8);
  const sortedTasks = [...tasks].sort((a, b) => {
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    if (a.dueAt && b.dueAt) return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    if (a.dueAt) return -1;
    if (b.dueAt) return 1;
    return 0;
  });

  const goalLink = (label: string) => (
    <Link
      component="button"
      type="button"
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); openGoalDialog(); }}
      underline="always"
      sx={{ color: tokens.champagne, fontWeight: 700, fontSize: 'inherit', verticalAlign: 'baseline', textDecorationColor: 'currentColor', '&:hover': { color: tokens.champagneSoft } }}
    >
      {label}
    </Link>
  );

  return (
    <Box>
      <PageHeader
        eyebrow={formatDateLong(new Date())}
        title={getGreeting(user?.firstName)}
        description={loading ? undefined : [salonName, dayState].filter(Boolean).join(' · ')}
        actions={canOperate ? (
          <>
            <Button variant="outlined" startIcon={<PointOfSaleOutlinedIcon />} onClick={() => navigate('/salon/sales')}>Nowa sprzedaż</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/salon/calendar')}>Nowa wizyta</Button>
          </>
        ) : undefined}
      />

      {loading ? (
        <LoadingState variant="page" count={4} rows={5} />
      ) : (
        <>
          <Box sx={tilesGridSx}>
            <StatTile
              tone="hero"
              label="Przychód dziś"
              value={formatMoney(stats.revenue)}
              progress={dailyGoal.revenue > 0 ? Math.min(100, (stats.revenue / dailyGoal.revenue) * 100) : undefined}
              hint={dailyGoal.revenue > 0 ? (
                <>{formatPercent(revenueGoalPct)} celu dnia ({formatMoney(dailyGoal.revenue)}) · {goalLink('Zmień')}</>
              ) : (
                <>Wizyty zakończone i sprzedaż w kasie · {goalLink('Ustaw cel dnia')}</>
              )}
              sx={{ gridColumn: { xs: 'span 2', lg: 'auto' } }}
            />
            <StatTile
              label="Wizyty dziś"
              value={formatNumber(stats.bookingsToday)}
              hint={`${formatNumber(dashStats.monthBookings)} w tym miesiącu · ${formatNumber(dashStats.totalBookings)} łącznie`}
              onClick={canOperate ? () => navigate('/salon/calendar') : undefined}
            />
            <StatTile
              label="Przychód w tym miesiącu"
              value={formatMoney(dashStats.monthRevenue)}
              hint={`z ${formatMoney(dashStats.yearRevenue)} w tym roku`}
              sx={{ gridColumn: { xs: 'span 2', lg: 'auto' }, order: { xs: 1, lg: 0 } }}
            />
            <StatTile
              label="Zadania na dziś"
              value={formatNumber(dueDone)}
              unit={`/ ${formatNumber(dueTodayTasks.length)}`}
              hint={!dueTodayTasks.length
                ? 'Brak zadań z terminem na dziś'
                : dueDone === dueTodayTasks.length
                  ? 'Wszystkie zrealizowane'
                  : `${countLabel(dueTodayTasks.length - dueDone, 'zadanie', 'zadania', 'zadań')} do zrobienia`}
              onClick={() => navigate('/tasks')}
            />
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={8}>
              <Stack spacing={2.5}>
                {/* HARMONOGRAM DNIA */}
                <SectionCard
                  title="Harmonogram dnia"
                  subtitle={todayBookings.length
                    ? `${countLabel(todayBookings.length, 'wizyta', 'wizyty', 'wizyt')}${upcoming.length ? ` · ${upcoming.length} ${plural(upcoming.length, 'nadchodząca', 'nadchodzące', 'nadchodzących')}` : ''}`
                    : undefined}
                  padding="none"
                  action={canOperate ? (
                    <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/salon/calendar')}>Kalendarz</Button>
                  ) : undefined}
                >
                  {todayBookings.length === 0 ? (
                    <EmptyState
                      compact
                      icon={<EventAvailableOutlinedIcon />}
                      title="Na dziś nie ma jeszcze wizyt"
                      description={canOperate
                        ? (bookingLink ? 'Dodaj wizytę w kalendarzu albo wyślij klientom link do rezerwacji online.' : 'Dodaj wizytę w kalendarzu — pojawi się tutaj razem z godziną i pracownikiem.')
                        : 'Wizyty zaplanowane na dziś pojawią się tutaj.'}
                      actions={canOperate ? (
                        <>
                          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/salon/calendar')}>Nowa wizyta</Button>
                          {bookingLink && (
                            <Button startIcon={<LinkOutlinedIcon />} onClick={copyBookingLink}>Kopiuj link do rezerwacji</Button>
                          )}
                        </>
                      ) : undefined}
                    />
                  ) : (
                    <>
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {visibleBookings.map((booking) => {
                          const isPast = bookingEnd(booking) < now;
                          const inProgress = booking.status === 'IN_PROGRESS';
                          const staffFirst = booking.staff?.firstName ?? booking.staff?.user?.firstName;
                          const staffLast = booking.staff?.lastName ?? booking.staff?.user?.lastName;
                          const serviceLine = [booking.service?.name, booking.service?.duration ? formatDuration(booking.service.duration) : null].filter(Boolean).join(' · ');
                          const content = (
                            <Stack direction="row" alignItems="center" gap={{ xs: 1.5, sm: 2 }} sx={{ width: '100%', px: { xs: 2, md: 2.5 }, py: 1.5, minWidth: 0 }}>
                              <Box sx={{ width: 48, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.3, color: inProgress ? tokens.plum : tokens.ink }}>
                                  {booking.startTime || '—'}
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ lineHeight: 1.3 }}>{booking.endTime || ''}</Typography>
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 650, lineHeight: 1.35 }} noWrap>
                                  {[booking.client?.firstName, booking.client?.lastName].filter(Boolean).join(' ') || 'Klient bez nazwiska'}
                                </Typography>
                                <Typography variant="caption" component="div" noWrap>{serviceLine || '—'}</Typography>
                                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                                  <BookingStatusPill status={booking.status} size="small" />
                                </Box>
                              </Box>
                              <Stack direction="row" alignItems="center" gap={1} sx={{ width: { xs: 'auto', sm: 150 }, minWidth: 0, flexShrink: 0 }}>
                                <PersonAvatar firstName={staffFirst} lastName={staffLast} fallback="?" seed={booking.staffId} size={28} />
                                <Typography variant="body2" noWrap sx={{ display: { xs: 'none', sm: 'block' }, color: tokens.inkSoft }}>
                                  {staffFirst ? `${staffFirst}${staffLast ? ` ${staffLast.charAt(0)}.` : ''}` : 'Bez pracownika'}
                                </Typography>
                              </Stack>
                              <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 150, flexShrink: 0 }}>
                                <BookingStatusPill status={booking.status} size="small" />
                              </Box>
                              <Typography sx={{ display: { xs: 'none', md: 'block' }, width: 76, flexShrink: 0, textAlign: 'right', fontSize: '0.9375rem', fontWeight: 650, fontVariantNumeric: 'tabular-nums' }}>
                                {booking.totalPrice ? formatMoney(booking.totalPrice) : '—'}
                              </Typography>
                            </Stack>
                          );
                          return (
                            <Box
                              component="li"
                              key={booking.id}
                              sx={{
                                borderBottom: `1px solid ${tokens.line}`,
                                '&:last-of-type': { borderBottom: 0 },
                                bgcolor: inProgress ? tokens.plumSofter : 'transparent',
                                boxShadow: inProgress ? `inset 3px 0 0 ${tokens.plum}` : 'none',
                                opacity: isPast && booking.status === 'COMPLETED' ? 0.65 : 1,
                              }}
                            >
                              {canOperate ? (
                                <ButtonBase
                                  onClick={() => navigate(`/salon/calendar?${new URLSearchParams({ bookingId: booking.id, date: (booking.date || '').slice(0, 10) }).toString()}`)}
                                  sx={{ width: '100%', textAlign: 'left', display: 'block', transition: 'background-color .15s ease', '&:hover': { bgcolor: tokens.surface } }}
                                  aria-label={`Otwórz wizytę o ${booking.startTime} w kalendarzu`}
                                >
                                  {content}
                                </ButtonBase>
                              ) : content}
                            </Box>
                          );
                        })}
                      </Box>
                      {todayBookings.length > 8 && (
                        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line}`, textAlign: 'center' }}>
                          <Button size="small" onClick={() => setShowAllBookings((v) => !v)}>
                            {showAllBookings ? 'Pokaż mniej' : `Pokaż jeszcze ${countLabel(todayBookings.length - 8, 'wizytę', 'wizyty', 'wizyt')}`}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </SectionCard>

                {/* ZADANIA */}
                <SectionCard
                  title="Zadania"
                  subtitle={`${countLabel(tasks.length, 'otwarte', 'otwarte', 'otwartych')}${overdueCount > 0 ? ` · ${overdueCount} po terminie` : ''}`}
                  padding="none"
                  action={<Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/tasks')}>Wszystkie</Button>}
                >
                  {tasks.length === 0 ? (
                    <EmptyState
                      compact
                      icon={<TaskAltOutlinedIcon />}
                      title="Brak otwartych zadań"
                      description="Nowe zadania dla zespołu dodasz w module Zadania."
                    />
                  ) : (
                    <>
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                        {sortedTasks.slice(0, 8).map((t) => {
                          const assignees = t.assignees.map((a) => personName(a)).join(', ');
                          return (
                            <Box component="li" key={t.id} sx={{ borderBottom: `1px solid ${tokens.line}`, '&:last-of-type': { borderBottom: 0 } }}>
                              <ButtonBase
                                onClick={() => openTaskDetail(t.id)}
                                sx={{
                                  width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1.5,
                                  px: { xs: 2, md: 2.5 }, py: 1.25, transition: 'background-color .15s ease', '&:hover': { bgcolor: tokens.surface },
                                }}
                              >
                                <Box
                                  title={`Priorytet: ${PRIORITY_LABEL[t.priority]}`}
                                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_DOT[t.priority], flexShrink: 0 }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 }} noWrap>{t.title}</Typography>
                                  <Typography variant="caption" component="div" noWrap>
                                    {assignees || 'Nieprzypisane'}
                                    {t.dueAt && ` · termin ${formatDate(t.dueAt)}`}
                                  </Typography>
                                </Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-end', sm: 'center' }} gap={0.75} sx={{ flexShrink: 0 }}>
                                  {t.isOverdue && <StatusPill size="small" tone="danger" label="Po terminie" />}
                                  <StatusPill size="small" tone={TASK_STATUS_TONE[t.status] || 'neutral'} label={STATUS_LABEL[t.status] || t.status} />
                                </Stack>
                              </ButtonBase>
                            </Box>
                          );
                        })}
                      </Box>
                      {tasks.length > 8 && (
                        <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${tokens.line}`, textAlign: 'center' }}>
                          <Button size="small" onClick={() => navigate('/tasks')}>
                            Pokaż jeszcze {countLabel(tasks.length - 8, 'zadanie', 'zadania', 'zadań')}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </SectionCard>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack spacing={2.5}>
                {/* REALIZACJA DNIA */}
                <SectionCard
                  title="Realizacja dnia"
                  subtitle={`${countLabel(todayClientsCount, 'klient', 'klientów', 'klientów')} dziś · ${formatNumber(stats.clients)} w bazie`}
                  action={<Button size="small" startIcon={<EditOutlinedIcon />} onClick={openGoalDialog}>Cel dnia</Button>}
                >
                  <Stack spacing={2}>
                    {dailyGoal.revenue > 0 && (
                      <ProgressRow
                        label="Cel przychodu"
                        valueText={`${formatMoney(stats.revenue)} z ${formatMoney(dailyGoal.revenue)} · ${formatPercent(revenueGoalPct)}`}
                        value={(stats.revenue / dailyGoal.revenue) * 100}
                      />
                    )}
                    {dailyGoal.visits > 0 && (
                      <ProgressRow
                        label="Cel wizyt (zakończone)"
                        valueText={`${completedCount} z ${dailyGoal.visits} · ${formatPercent(visitsGoalPct)}`}
                        value={(completedCount / dailyGoal.visits) * 100}
                      />
                    )}
                    {dailyGoal.revenue === 0 && dailyGoal.visits === 0 && (
                      <Typography variant="body2" sx={{ color: tokens.inkSoft }}>
                        Nie ustawiono celu dnia. Ustaw cel przychodu i liczby wizyt, a zobaczysz tu postęp.
                      </Typography>
                    )}
                    <ProgressRow
                      label="Zakończone wizyty"
                      valueText={`${completedCount} z ${stats.bookingsToday}`}
                      value={occupancy}
                      color={tokens.success}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                      <MiniStat value={upcoming.length} label="Przed nami" />
                      <MiniStat value={completedCount} label="Zakończone" color={tokens.success} />
                      <MiniStat value={cancelledCount} label="Anulowane" color={tokens.danger} />
                    </Box>
                  </Stack>
                </SectionCard>

                {/* ZESPOL */}
                {staffMembers.length > 0 && (
                  <SectionCard
                    title="Zespół"
                    subtitle={`${countLabel(staffMembers.length, 'osoba', 'osoby', 'osób')} · wizyty dziś`}
                    padding="none"
                  >
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, py: 0.5 }}>
                      {staffMembers.slice(0, 5).map((s: any) => {
                        const staffBookings = todayBookings.filter((b) => b.staffId === s.id);
                        const row = (
                          <Stack direction="row" alignItems="center" gap={1.5} sx={{ width: '100%', px: { xs: 2, md: 2.5 }, py: 1, minWidth: 0 }}>
                            <PersonAvatar firstName={s.firstName} lastName={s.lastName} seed={s.id} size={36} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 }} noWrap>
                                {s.firstName} {s.lastName}
                              </Typography>
                              <Typography variant="caption" component="div" noWrap>{s.specialization || 'Pracownik'}</Typography>
                            </Box>
                            <StatusPill
                              size="small"
                              noDot
                              tone={staffBookings.length ? 'plum' : 'neutral'}
                              label={countLabel(staffBookings.length, 'wizyta', 'wizyty', 'wizyt')}
                            />
                          </Stack>
                        );
                        return (
                          <Box component="li" key={s.id}>
                            {canOpenEmployee ? (
                              <ButtonBase
                                onClick={() => navigate(`/employees?staffId=${s.id}`)}
                                sx={{ width: '100%', textAlign: 'left', display: 'block', transition: 'background-color .15s ease', '&:hover': { bgcolor: tokens.surface } }}
                              >
                                {row}
                              </ButtonBase>
                            ) : row}
                          </Box>
                        );
                      })}
                    </Box>
                  </SectionCard>
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      )}

      {/* Szczegóły zadania */}
      <Dialog open={!!taskDetail || taskDetailLoading} onClose={() => setTaskDetail(null)} fullWidth maxWidth="sm">
        {taskDetailLoading || !taskDetail ? (
          <DialogContent><Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box></DialogContent>
        ) : (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" gap={1.25}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PRIORITY_DOT[taskDetail.priority], flexShrink: 0 }} />
                <Box component="span" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{taskDetail.title}</Box>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  <StatusPill tone={TASK_STATUS_TONE[taskDetail.status] || 'neutral'} label={STATUS_LABEL[taskDetail.status] || taskDetail.status} />
                  <StatusPill tone="neutral" noDot label={`Priorytet: ${PRIORITY_LABEL[taskDetail.priority]}`} />
                  {taskDetail.category && <StatusPill tone="neutral" noDot label={taskDetail.category.name} />}
                  {taskDetail.isOverdue && <StatusPill tone="danger" label="Po terminie" />}
                </Stack>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
                  <Box>
                    <Typography variant="caption" component="div">Termin</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {taskDetail.dueAt
                        ? formatDate(taskDetail.dueAt) + (taskDetail.dueHasTime ? `, ${formatTime(taskDetail.dueAt)}` : '')
                        : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" component="div">Utworzył(a)</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{personName(taskDetail.createdBy)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" component="div">Przypisani</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                      {taskDetail.assignees.length ? taskDetail.assignees.map((a) => personName(a)).join(', ') : 'Nieprzypisane'}
                    </Typography>
                  </Box>
                </Box>

                {taskDetail.description && (
                  <Box>
                    <Typography variant="caption" component="div">Opis</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{taskDetail.description}</Typography>
                  </Box>
                )}

                {taskDetail.checklist.length > 0 && (
                  <Box>
                    <Typography variant="caption" component="div">
                      Lista kontrolna ({taskDetail.checklistProgress.done} z {taskDetail.checklistProgress.total})
                    </Typography>
                    <Stack spacing={0.5} mt={0.75}>
                      {taskDetail.checklist.map((item: any) => (
                        <Stack key={item.id} direction="row" spacing={1} alignItems="flex-start">
                          {item.isDone
                            ? <CheckCircleIcon sx={{ fontSize: 20, color: tokens.success, mt: '1px' }} />
                            : <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: tokens.subtle, mt: '1px' }} />}
                          <Typography variant="body2" sx={{ textDecoration: item.isDone ? 'line-through' : 'none', color: item.isDone ? tokens.muted : tokens.ink }}>
                            {item.content || item.title || item.text || item.label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTaskDetail(null)}>Zamknij</Button>
              <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => { const id = taskDetail.id; setTaskDetail(null); navigate(`/tasks/${id}`); }}>
                Otwórz w zadaniach
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Cel dnia — okno ustawień */}
      <Dialog open={goalOpen} onClose={() => setGoalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ustaw cel dnia</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              type="number" fullWidth label="Cel przychodu"
              value={goalForm.revenue} onChange={(e) => setGoalForm({ ...goalForm, revenue: e.target.value })}
              placeholder="np. 2000"
              InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
              inputProps={{ min: 0 }}
            />
            <TextField
              type="number" fullWidth label="Cel liczby zakończonych wizyt"
              value={goalForm.visits} onChange={(e) => setGoalForm({ ...goalForm, visits: e.target.value })}
              placeholder="np. 12"
              InputProps={{ endAdornment: <InputAdornment position="end">wizyt</InputAdornment> }}
              inputProps={{ min: 0 }}
              helperText="Wpisz 0 lub zostaw puste pole, aby wyłączyć dany cel."
            />
            <Typography variant="body2" sx={{ color: tokens.muted }}>
              Cel zapisuje się dla tego salonu na tym urządzeniu i pokazuje postęp na Panelu.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalOpen(false)}>Anuluj</Button>
          <Button variant="contained" onClick={saveGoal}>Zapisz cel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

/* ---------- MARKETER / IT ---------- */
const ROLE_SHORTCUTS: Record<'MARKETER' | 'IT', { description: string; items: { icon: React.ReactNode; title: string; description: string; to: string }[] }> = {
  MARKETER: {
    description: 'Klienci, kampanie i karty podarunkowe salonu — wszystko, czego potrzebujesz na co dzień.',
    items: [
      { icon: <PeopleAltOutlinedIcon />, title: 'Klienci', description: 'Baza klientów salonu z historią wizyt.', to: '/salon/clients' },
      { icon: <CampaignOutlinedIcon />, title: 'Kampanie', description: 'Przyciągaj nowych klientów i przypominaj się stałym.', to: '/salon/marketing' },
      { icon: <CardGiftcardOutlinedIcon />, title: 'Karty i pakiety', description: 'Karty podarunkowe i pakiety usług.', to: '/salon/gifts' },
      { icon: <TaskAltOutlinedIcon />, title: 'Zadania', description: 'Twoje zadania i ich terminy.', to: '/tasks' },
    ],
  },
  IT: {
    description: 'Dane salonu i jego strona w internecie w jednym miejscu.',
    items: [
      { icon: <BusinessOutlinedIcon />, title: 'Profil biznesu', description: 'Dane firmy, adres, kontakt i godziny otwarcia.', to: '/salon/profile' },
      { icon: <LanguageOutlinedIcon />, title: 'Wizytówka WWW', description: 'Publiczna strona salonu z ofertą i rezerwacją online.', to: '/salon/website' },
      { icon: <TaskAltOutlinedIcon />, title: 'Zadania', description: 'Twoje zadania i ich terminy.', to: '/tasks' },
    ],
  },
};

const RoleWelcomeDashboard: React.FC<{ role: 'MARKETER' | 'IT' }> = ({ role }) => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const config = ROLE_SHORTCUTS[role];
  return (
    <Box>
      <PageHeader eyebrow={formatDateLong(new Date())} title={getGreeting(user?.firstName)} description={config.description} />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: `repeat(${Math.min(config.items.length, 4)}, minmax(0, 1fr))` } }}>
        {config.items.map((item) => (
          <ShortcutCard key={item.to} icon={item.icon} title={item.title} description={item.description} onClick={() => navigate(item.to)} />
        ))}
      </Box>
    </Box>
  );
};

/* ---------- CLIENT ---------- */
const ClientDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [upcoming, setUpcoming] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/bookings/my', { params: { limit: 100 } })
      .then((r) => {
        const bookings = r.data.data || [];
        const now = new Date();
        setUpcoming(bookings.filter((b: any) => new Date(b.date) >= now && !['CANCELLED', 'COMPLETED', 'NO_SHOW'].includes(b.status)).length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      <PageHeader
        eyebrow={formatDateLong(new Date())}
        title={getGreeting(user?.firstName)}
        description="Umów wizytę w ulubionym salonie albo sprawdź swoje rezerwacje."
        actions={<Button variant="contained" startIcon={<StorefrontOutlinedIcon />} onClick={() => navigate('/salons')}>Znajdź salon</Button>}
      />

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
        <StatTile
          tone="hero"
          label="Nadchodzące wizyty"
          value={formatNumber(upcoming)}
          hint={upcoming ? 'Szczegóły i odwołanie wizyty znajdziesz w „Moich wizytach”.' : 'Nie masz zaplanowanych wizyt.'}
          loading={loading}
          onClick={() => navigate('/bookings/my')}
          sx={{ gridColumn: { xs: 'auto', sm: 'span 2', lg: 'auto' } }}
        />
        <ShortcutCard
          icon={<StorefrontOutlinedIcon />}
          title="Znajdź salon"
          description="Przeglądaj salony, sprawdź ofertę i zarezerwuj termin."
          onClick={() => navigate('/salons')}
        />
        <ShortcutCard
          icon={<EventNoteOutlinedIcon />}
          title="Moje wizyty"
          description="Nadchodzące i minione rezerwacje w jednym miejscu."
          onClick={() => navigate('/bookings/my')}
        />
      </Box>
    </Box>
  );
};

/* ---------- MAIN DASHBOARD ---------- */
export const DashboardPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;

  if (role === 'SUPER_ADMIN') return <SuperAdminDashboard />;
  if (role === 'SALON_OWNER' || role === 'STAFF' || role === 'MANAGER' || role === 'ACCOUNTANT' || role === 'ANALYST')
    return <SalonOwnerDashboard />;
  if (role === 'MARKETER' || role === 'IT') return <RoleWelcomeDashboard role={role} />;
  if (role === 'CLIENT') return <ClientDashboard />;

  return (
    <Box>
      <Alert severity="info">Twoja rola nie została rozpoznana. Skontaktuj się z administratorem.</Alert>
    </Box>
  );
};
